/**
 * InkDyno Brand + Color System v15.3
 *
 * Architecture:
 *   Brand × Size = Shopify variant options (Dawn handles these)
 *   Color        = Line item property via hidden input (this JS)
 *
 * This JS handles:
 *   1. CSS for clean circle swatches (variant swatches + our color swatches)
 *   2. Color swatch rendering per brand (from #inkdyno-brand-colors JSON)
 *      → FILTERED to only show colors that have a product image (contrast-aware)
 *   3. Hidden input properties[Color] management
 *   4. Product gallery filtering by brand+color (using alt tag metadata)
 *   5. Brand change detection → re-render color swatches
 *
 * Image alt tag format: "brand:Bella Canvas|color:Navy|Product Title"
 */
(function () {
  'use strict';

  var brandColors = null;       // JSON config from brand-selector.liquid
  var selectedColor = null;     // currently selected color name
  var selectedBrand = null;     // currently selected brand from variant
  var allImages = [];           // parsed image data [{src, brand, color, el}]
  var hiddenInput = null;       // the properties[Color] hidden input
  var availableColors = {};     // {brand: {colorName: true}} built from product images
  var lastFilteredBrand = null;

  /* ── Scroll-freeze utilities ──
     Prevent ANY vertical page movement during gallery operations.
     Dawn's slider-component fires IntersectionObserver → slideChanged →
     setActiveThumbnail, and Dawn's setActiveMedia does window.scrollTo.
     We freeze scroll before ANY is-active / display changes and restore after. */
  var _scrollFrozen = false;
  var _frozenScrollY = 0;
  var _origScrollTo = null;

  function freezeScroll() {
    if (_scrollFrozen) return;
    _scrollFrozen = true;
    _frozenScrollY = window.scrollY;
    /* Monkey-patch window.scrollTo to suppress ALL scroll calls */
    _origScrollTo = window.scrollTo;
    window.scrollTo = function () { /* suppressed */ };
    window.scroll = function () { /* suppressed */ };
  }

  function unfreezeScroll() {
    if (!_scrollFrozen) return;
    _scrollFrozen = false;
    window.scrollTo = _origScrollTo || window.scrollTo;
    window.scroll = _origScrollTo || window.scroll;
    /* Restore exact scroll position in case anything moved it */
    _origScrollTo.call(window, 0, _frozenScrollY);
    _origScrollTo = null;
  }

  /* ═══════════════════════════════════════════════════════════
     1. CSS INJECTION — Clean circle swatches
     Applies to both Dawn's variant swatches AND our custom color swatches
     ═══════════════════════════════════════════════════════════ */
  function injectCSS() {
    if (document.getElementById('inkdyno-swatch-css')) return;

    var css = '\n/* === InkDyno Swatches v11 === */\n';

    /* ── Dawn variant swatch size: 44px ── */
    css += '.product-form__input--swatch .swatch-input__input + .swatch-input__label {\n';
    css += '  --swatch-input--size: 4.4rem !important;\n';
    css += '  margin: 0.6rem 0.8rem 0.4rem 0 !important;\n';
    css += '  outline: none !important;\n';
    css += '  box-shadow: none !important;\n';
    css += '  transition: box-shadow 0.2s ease !important;\n';
    css += '  position: relative !important;\n';
    css += '  overflow: visible !important;\n';
    css += '}\n';

    css += '.product-form__input--swatch .swatch-input__label .swatch {\n';
    css += '  border: 1px solid rgba(0,0,0,0.08) !important;\n';
    css += '}\n';

    /* Light colors */
    css += '.product-form__input--swatch .swatch-input__label .swatch[style*="F5F5F5"],\n';
    css += '.product-form__input--swatch .swatch-input__label .swatch[style*="F3EBDA"],\n';
    css += '.product-form__input--swatch .swatch-input__label .swatch[style*="F5F0E8"],\n';
    css += '.product-form__input--swatch .swatch-input__label .swatch[style*="F5E6A8"] {\n';
    css += '  border: 1px solid rgba(0,0,0,0.18) !important;\n';
    css += '}\n';

    /* Hover */
    css += '.product-form__input--swatch .swatch-input__input + .swatch-input__label:hover,\n';
    css += '.product-form__input--swatch .swatch-input__input:hover + .swatch-input__label {\n';
    css += '  outline: none !important;\n';
    css += '  box-shadow: 0 0 0 2px #fff, 0 0 0 3.5px rgba(0,0,0,0.25) !important;\n';
    css += '}\n';

    /* Selected */
    css += '.product-form__input--swatch .swatch-input__input:checked + .swatch-input__label,\n';
    css += '.product-form__input--swatch .swatch-input__input:active + .swatch-input__label {\n';
    css += '  outline: none !important;\n';
    css += '  box-shadow: 0 0 0 2px #fff, 0 0 0 4px #333 !important;\n';
    css += '}\n';

    /* Focus-visible */
    css += '.product-form__input--swatch .swatch-input__input:focus-visible + .swatch-input__label {\n';
    css += '  outline: none !important;\n';
    css += '  box-shadow: 0 0 0 2px #fff, 0 0 0 4px #333, 0 0 0 6px rgba(59,130,246,0.5) !important;\n';
    css += '}\n';

    /* Disabled */
    css += '.product-form__input--swatch .swatch-input__input:disabled + .swatch-input__label {\n';
    css += '  opacity: 0.3 !important;\n';
    css += '  cursor: not-allowed !important;\n';
    css += '}\n';

    /* ── Tooltip on hover ── */
    css += '.product-form__input--swatch .swatch-input__label[title]:hover::after {\n';
    css += '  content: attr(title);\n';
    css += '  position: absolute;\n';
    css += '  bottom: calc(100% + 8px);\n';
    css += '  left: 50%;\n';
    css += '  transform: translateX(-50%);\n';
    css += '  background: #333;\n';
    css += '  color: #fff;\n';
    css += '  font-size: 11px;\n';
    css += '  line-height: 1.3;\n';
    css += '  padding: 4px 10px;\n';
    css += '  border-radius: 4px;\n';
    css += '  white-space: nowrap;\n';
    css += '  z-index: 10;\n';
    css += '  pointer-events: none;\n';
    css += '}\n';

    /* ── Color label: fixed-height <div> (not <legend> which browsers ignore height on) ── */
    /* ── Prevent scroll anchoring from causing shifts ── */
    css += '#inkdyno-color-selector { overflow-anchor: none; contain: layout style; }\n';
    css += 'media-gallery { overflow-anchor: none; }\n';
    css += '.inkdyno-color-label {\n';
    css += '  display: block !important;\n';
    css += '  height: 20px !important;\n';
    css += '  line-height: 20px !important;\n';
    css += '  font-size: 1.3rem !important;\n';
    css += '  letter-spacing: 0.04rem !important;\n';
    css += '  margin-bottom: 6px !important;\n';
    css += '  white-space: nowrap !important;\n';
    css += '  overflow: hidden !important;\n';
    css += '  text-overflow: ellipsis !important;\n';
    css += '  max-width: 100% !important;\n';
    css += '  box-sizing: border-box !important;\n';
    css += '  padding: 0 !important;\n';
    css += '}\n';

    /* ── Fallback: target <legend> too (in case JS hasn't replaced it yet) ── */
    css += '#inkdyno-color-selector > legend.form__label {\n';
    css += '  white-space: nowrap !important;\n';
    css += '  overflow: hidden !important;\n';
    css += '  text-overflow: ellipsis !important;\n';
    css += '  font-size: 1.3rem !important;\n';
    css += '  letter-spacing: 0.04rem !important;\n';
    css += '  margin-bottom: 6px !important;\n';
    css += '  max-width: 100% !important;\n';
    css += '  padding: 0 !important;\n';
    css += '}\n';

    /* ── Our custom color swatches (inkdyno-swatch-btn) ── */
    css += '#inkdyno-color-swatches {\n';
    css += '  display: flex;\n';
    css += '  flex-wrap: wrap;\n';
    css += '  gap: 0;\n';
    css += '  padding: 0.4rem 0;\n';
    css += '}\n';

    css += '.inkdyno-swatch-btn {\n';
    css += '  appearance: none;\n';
    css += '  -webkit-appearance: none;\n';
    css += '  background: none;\n';
    css += '  border: none;\n';
    css += '  padding: 0;\n';
    css += '  margin: 0.6rem 0.8rem 0.4rem 0;\n';
    css += '  cursor: pointer;\n';
    css += '  width: 4.4rem;\n';
    css += '  height: 4.4rem;\n';
    css += '  border-radius: 50%;\n';
    css += '  position: relative;\n';
    css += '  overflow: visible;\n';
    css += '  outline: none;\n';
    css += '  box-shadow: none;\n';
    css += '  transition: box-shadow 0.2s ease;\n';
    css += '}\n';

    css += '.inkdyno-swatch-btn .inkdyno-swatch-dot {\n';
    css += '  display: block;\n';
    css += '  width: 100%;\n';
    css += '  height: 100%;\n';
    css += '  border-radius: 50%;\n';
    css += '  border: 1px solid rgba(0,0,0,0.08);\n';
    css += '}\n';

    /* Light colors: visible border */
    css += '.inkdyno-swatch-btn.inkdyno-swatch--light .inkdyno-swatch-dot {\n';
    css += '  border: 1px solid rgba(0,0,0,0.18);\n';
    css += '}\n';

    /* Hover */
    css += '.inkdyno-swatch-btn:hover {\n';
    css += '  box-shadow: 0 0 0 2px #fff, 0 0 0 3.5px rgba(0,0,0,0.25);\n';
    css += '}\n';

    /* Selected */
    css += '.inkdyno-swatch-btn.inkdyno-swatch--selected {\n';
    css += '  box-shadow: 0 0 0 2px #fff, 0 0 0 4px #333;\n';
    css += '}\n';

    /* Focus-visible */
    css += '.inkdyno-swatch-btn:focus-visible {\n';
    css += '  box-shadow: 0 0 0 2px #fff, 0 0 0 4px #333, 0 0 0 6px rgba(59,130,246,0.5);\n';
    css += '}\n';

    /* Tooltip */
    css += '.inkdyno-swatch-btn[title]:hover::after {\n';
    css += '  content: attr(title);\n';
    css += '  position: absolute;\n';
    css += '  bottom: calc(100% + 8px);\n';
    css += '  left: 50%;\n';
    css += '  transform: translateX(-50%);\n';
    css += '  background: #333;\n';
    css += '  color: #fff;\n';
    css += '  font-size: 11px;\n';
    css += '  line-height: 1.3;\n';
    css += '  padding: 4px 10px;\n';
    css += '  border-radius: 4px;\n';
    css += '  white-space: nowrap;\n';
    css += '  z-index: 10;\n';
    css += '  pointer-events: none;\n';
    css += '}\n';

    /* Hide "visually-hidden" inside swatch labels */
    css += '.product-form__input--swatch .swatch-input__label .visually-hidden { display: none !important; }\n';

    /* ── Modal lightbox: only show the clicked image, not all ── */
    css += '.product-media-modal__content > *:not(.active) { display: none !important; }\n';
    css += '.product-media-modal__content > *.active { display: block !important; }\n';

    var style = document.createElement('style');
    style.id = 'inkdyno-swatch-css';
    style.textContent = css;
    document.head.appendChild(style);
  }


  /* ═══════════════════════════════════════════════════════════
     2. LOAD BRAND COLOR CONFIG + SCAN PRODUCT IMAGES
     ═══════════════════════════════════════════════════════════ */
  function loadBrandColors() {
    var el = document.getElementById('inkdyno-brand-colors');
    if (!el) return false;
    try {
      brandColors = JSON.parse(el.textContent);
    } catch (e) {
      console.warn('[InkDyno] Failed to parse brand colors JSON', e);
      return false;
    }

    /* Scan product images to build available colors map.
       Only colors with an actual mockup image will appear in the palette.
       This handles contrast filtering — skipped colors have no image. */
    buildAvailableColors();
    return true;
  }

  function buildAvailableColors() {
    availableColors = {};
    /* Scan all img elements inside the product gallery + modal */
    var imgs = document.querySelectorAll(
      'media-gallery img, product-modal img, .product__media-list img'
    );
    imgs.forEach(function (img) {
      var alt = img.alt || img.getAttribute('alt') || '';
      var meta = parseImageAlt(alt);
      if (meta.brand && meta.color) {
        if (!availableColors[meta.brand]) availableColors[meta.brand] = {};
        availableColors[meta.brand][meta.color] = true;
      }
    });

    /* Log summary */
    var total = 0;
    Object.keys(availableColors).forEach(function (b) {
      var n = Object.keys(availableColors[b]).length;
      total += n;
    });
    console.log('[InkDyno] Available colors from images:', total, availableColors);
  }


  /* ═══════════════════════════════════════════════════════════
     3. DETECT SELECTED BRAND FROM VARIANT PICKER
     Brand is Option 1 in the variant selects
     ═══════════════════════════════════════════════════════════ */
  function getSelectedBrand() {
    var vs = document.querySelector('variant-selects');
    if (!vs) return null;

    /* Find first fieldset (Brand option) — look for checked radio or selected option */
    var fieldsets = vs.querySelectorAll('fieldset, .product-form__input');
    for (var i = 0; i < fieldsets.length; i++) {
      var legend = fieldsets[i].querySelector('legend, label');
      if (!legend) continue;
      var text = legend.textContent.toLowerCase();
      if (text.indexOf('brand') !== -1 || text.indexOf('marka') !== -1) {
        /* Radio buttons */
        var checked = fieldsets[i].querySelector('input[type="radio"]:checked');
        if (checked) return checked.value;
        /* Dropdown */
        var select = fieldsets[i].querySelector('select');
        if (select) return select.value;
      }
    }

    /* Fallback: first option's checked radio */
    var firstChecked = vs.querySelector('fieldset input[type="radio"]:checked');
    if (firstChecked) return firstChecked.value;

    return null;
  }


  /* ═══════════════════════════════════════════════════════════
     4. RENDER COLOR SWATCHES FOR SELECTED BRAND
     ═══════════════════════════════════════════════════════════ */
  function renderColorSwatches(brand) {
    var container = document.getElementById('inkdyno-color-swatches');
    var selector = document.getElementById('inkdyno-color-selector');
    var nameSpan = document.getElementById('inkdyno-selected-color-name');
    if (!container || !selector) return;

    var allColors = brandColors[brand];
    if (!allColors || allColors.length === 0) {
      selector.style.display = 'none';
      return;
    }

    /* Filter to only colors that have an actual product image.
       This prevents showing colors skipped by contrast detection. */
    var brandAvailable = availableColors[brand] || {};
    var colors = allColors.filter(function (c) {
      return !!brandAvailable[c.name];
    });

    if (colors.length === 0) {
      /* Fallback: if no images detected (edge case), show all */
      colors = allColors;
    }

    selector.style.display = '';
    container.innerHTML = '';

    /* Detect light colors needing a visible border using perceived brightness */
    function isLightColor(hex) {
      var r = parseInt(hex.substr(1, 2), 16);
      var g = parseInt(hex.substr(3, 2), 16);
      var b = parseInt(hex.substr(5, 2), 16);
      return (r * 299 + g * 587 + b * 114) / 1000 > 180;
    }

    colors.forEach(function (c) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'inkdyno-swatch-btn';
      btn.setAttribute('title', c.name);
      btn.setAttribute('data-color-slug', c.slug);
      btn.setAttribute('data-color-name', c.name);
      btn.setAttribute('aria-label', 'Color: ' + c.name);

      if (isLightColor(c.hex)) {
        btn.classList.add('inkdyno-swatch--light');
      }

      var dot = document.createElement('span');
      dot.className = 'inkdyno-swatch-dot';
      dot.style.background = c.hex;
      btn.appendChild(dot);

      btn.addEventListener('click', function () {
        selectColor(c.name, c.slug, brand);
      });

      container.appendChild(btn);
    });

    /* If user already picked a color for this brand, restore it */
    var preserve = null;
    if (selectedColor) {
      preserve = colors.find(function (c) { return c.name === selectedColor; });
    }
    var autoSelect = preserve || colors.find(function (c) { return c.hasImage; }) || colors[0];
    selectColor(autoSelect.name, autoSelect.slug, brand);
  }


  /* ═══════════════════════════════════════════════════════════
     5. SELECT A COLOR
     ═══════════════════════════════════════════════════════════ */
  function selectColor(name, slug, brand) {
    selectedColor = name;

    /* Update UI: selected state */
    var container = document.getElementById('inkdyno-color-swatches');
    if (container) {
      container.querySelectorAll('.inkdyno-swatch-btn').forEach(function (btn) {
        btn.classList.toggle('inkdyno-swatch--selected',
          btn.getAttribute('data-color-slug') === slug);
      });
    }

    /* Update label */
    var nameSpan = document.getElementById('inkdyno-selected-color-name');
    if (nameSpan) nameSpan.textContent = name;

    /* Set hidden input */
    ensureHiddenInput();
    if (hiddenInput) hiddenInput.value = name;

    /* Filter gallery */
    filterGallery(brand, name);

    console.log('[InkDyno] Color selected:', brand, '→', name);
  }


  /* ═══════════════════════════════════════════════════════════
     6. ENSURE properties[Color] HIDDEN INPUT IN PRODUCT FORM
     Dawn may re-render the form on variant change, removing our input.
     So we must always verify the input is still in the DOM.
     ═══════════════════════════════════════════════════════════ */
  function ensureHiddenInput() {
    /* Check if our cached input is still in the live DOM */
    if (hiddenInput && hiddenInput.isConnected) return;

    /* Reset — the old reference is stale */
    hiddenInput = null;

    var form = document.querySelector('product-form form[action*="/cart/add"]');
    if (!form) {
      form = document.querySelector('form[action*="/cart/add"]');
    }
    if (!form) {
      console.warn('[InkDyno] No cart form found for properties[Color]');
      return;
    }

    /* Check if already exists (e.g. Liquid rendered it) */
    hiddenInput = form.querySelector('input[name="properties[Color]"]');
    if (hiddenInput) return;

    /* Create it */
    hiddenInput = document.createElement('input');
    hiddenInput.type = 'hidden';
    hiddenInput.name = 'properties[Color]';
    hiddenInput.value = selectedColor || '';
    form.appendChild(hiddenInput);
    console.log('[InkDyno] Created properties[Color] hidden input, value:', hiddenInput.value);
  }


  /* ═══════════════════════════════════════════════════════════
     7. GALLERY FILTERING BY BRAND + COLOR
     Images have alt: "brand:Bella Canvas|color:Navy|Title"
     
     Two-layer filtering:
       a) filterGalleryByBrand — hide/show items so only selected
          brand's images appear in gallery, thumbnails, AND modal
       b) filterGallery — calls (a) then scrolls to exact color
     ═══════════════════════════════════════════════════════════ */
  var lastFilteredBrand = null;

  function parseImageAlt(alt) {
    if (!alt) return {};
    var result = {};
    var parts = alt.split('|');
    parts.forEach(function (p) {
      var kv = p.split(':');
      if (kv.length === 2) {
        result[kv[0].trim().toLowerCase()] = kv[1].trim();
      }
    });
    return result;
  }

  /**
   * Hide gallery items, thumbnails and modal items that don't
   * belong to the selected brand.
   */
  function filterGalleryByBrand(brand) {
    if (!brand) return;
    var brandLower = brand.toLowerCase();
    if (lastFilteredBrand === brandLower) return;
    lastFilteredBrand = brandLower;

    var gallery = document.querySelector('media-gallery');
    if (!gallery) return;

    freezeScroll();

    /* ── 1. Main gallery viewer items ── */
    var viewerItems = gallery.querySelectorAll('.product__media-list > [data-media-id]');
    var visibleCount = 0;
    var firstVisible = null;
    var hasActiveVisible = false;
    viewerItems.forEach(function (item) {
      var img = item.querySelector('img');
      if (!img) { item.style.display = 'none'; return; }
      var meta = parseImageAlt(img.alt || '');
      var imgBrand = (meta.brand || '').toLowerCase();
      if (imgBrand === brandLower) {
        item.style.display = '';
        visibleCount++;
        if (!firstVisible) firstVisible = item;
        if (item.classList.contains('is-active')) hasActiveVisible = true;
      } else {
        item.style.display = 'none';
      }
    });

    /* Ensure at least one visible item is active BEFORE removing is-active from hidden items.
       Dawn CSS hides items without is-active, so we must never have 0 active items. */
    if (!hasActiveVisible && firstVisible) {
      firstVisible.classList.add('is-active');
    }

    /* NOW safe to remove is-active from hidden (non-brand) items */
    viewerItems.forEach(function (item) {
      if (item.style.display === 'none') {
        item.classList.remove('is-active');
      }
    });

    /* Scroll to active item */
    var activeItem = gallery.querySelector('.product__media-item.is-active');
    if (activeItem && activeItem.parentElement) {
      activeItem.parentElement.scrollTo({ left: activeItem.offsetLeft });
    }

    /* ── 2. Update slider counter ── */
    var counterTotal = gallery.querySelector('.slider-counter--total');
    if (counterTotal) counterTotal.textContent = visibleCount;

    /* ── 3. Thumbnails ── */
    var thumbItems = gallery.querySelectorAll('.thumbnail-list > [data-target]');
    thumbItems.forEach(function (li) {
      var img = li.querySelector('img');
      if (!img) { li.style.display = 'none'; return; }
      var meta = parseImageAlt(img.alt || '');
      var imgBrand = (meta.brand || '').toLowerCase();
      li.style.display = (imgBrand === brandLower) ? '' : 'none';
    });

    /* ── 4. Modal images (product-media-modal) ── */
    /* Modal children can be direct <img> or wrapper divs containing <img>.
       Use tagName check to handle both cases. */
    var modal = document.querySelector('product-modal .product-media-modal__content');
    if (modal) {
      for (var i = 0; i < modal.children.length; i++) {
        var child = modal.children[i];
        var img = (child.tagName === 'IMG') ? child : child.querySelector('img');
        if (!img) { child.style.display = 'none'; continue; }
        var meta = parseImageAlt(img.alt || '');
        var imgBrand = (meta.brand || '').toLowerCase();
        child.style.display = (imgBrand === brandLower) ? '' : 'none';
      }
    }

    console.log('[InkDyno] Gallery filtered → brand:', brand, '(' + visibleCount + ' images)');
    /* Unfreeze after a tick — Dawn's setTimeout(scrollTo) fires on next tick */
    setTimeout(unfreezeScroll, 50);
  }

  function filterGallery(brand, colorName) {
    /* First ensure only this brand's images are visible */
    filterGalleryByBrand(brand);

    freezeScroll();

    var gallery = document.querySelector('media-gallery');
    if (!gallery) { unfreezeScroll(); return; }

    var viewer = gallery.querySelector('[id^="GalleryViewer"]');
    if (!viewer) { setTimeout(unfreezeScroll, 50); return; }

    var items = viewer.querySelectorAll('[data-media-id]');
    if (items.length === 0) { setTimeout(unfreezeScroll, 50); return; }

    var exactMatch = null;
    var brandMatch = null;

    items.forEach(function (item) {
      if (item.style.display === 'none') return; /* skip hidden */
      var img = item.querySelector('img');
      if (!img) return;

      var meta = parseImageAlt(img.alt || img.getAttribute('alt') || '');
      var imgBrand = (meta.brand || '').toLowerCase();
      var imgColor = (meta.color || '').toLowerCase();

      if (imgBrand === brand.toLowerCase() && imgColor === colorName.toLowerCase()) {
        if (!exactMatch) exactMatch = item;
      }
      if (imgBrand === brand.toLowerCase()) {
        if (!brandMatch) brandMatch = item;
      }
    });

    var targetItem = exactMatch || brandMatch;
    if (!targetItem) { setTimeout(unfreezeScroll, 50); return; }

    var mediaId = targetItem.dataset.mediaId;
    if (!mediaId) { setTimeout(unfreezeScroll, 50); return; }

    /* Swap is-active: add to target FIRST to prevent layout collapse.
       Dawn CSS: .product--thumbnail_slider .product__media-item:not(.is-active) { display:none }
       If we remove all first, gallery briefly has 0 visible items → collapses → shift. */
    targetItem.classList.add('is-active');
    viewer.querySelectorAll('[data-media-id]').forEach(function (el) {
      if (el !== targetItem) el.classList.remove('is-active');
    });

    /* Horizontal scroll only (no page scroll) */
    if (targetItem.parentElement) {
      targetItem.parentElement.scrollTo({ left: targetItem.offsetLeft });
    }

    /* Update thumbnail active state */
    var thumbContainer = gallery.querySelector('[id^="GalleryThumbnails"]');
    if (thumbContainer) {
      thumbContainer.querySelectorAll('button').forEach(function (b) {
        b.removeAttribute('aria-current');
      });
      var activeLi = thumbContainer.querySelector('[data-target="' + mediaId + '"]');
      if (activeLi) {
        var thumbBtn = activeLi.querySelector('button');
        if (thumbBtn) thumbBtn.setAttribute('aria-current', 'true');
        /* Scroll thumbnail into view */
        var slider = thumbContainer.querySelector('.slider') || thumbContainer;
        if (slider && slider.scrollTo) {
          slider.scrollTo({ left: activeLi.offsetLeft });
        }
      }
    }
    console.log('[InkDyno] Gallery →', mediaId);
    /* Unfreeze scroll after Dawn's async handlers settle */
    setTimeout(unfreezeScroll, 50);
  }


  /* ═══════════════════════════════════════════════════════════
     8. BRAND CHANGE LISTENER
     Watches variant-selects for brand option changes
     ═══════════════════════════════════════════════════════════ */
  function watchBrandChanges() {
    var vs = document.querySelector('variant-selects');
    if (!vs) return;

    /* Listen to change events on radio inputs and selects within variant-selects */
    vs.addEventListener('change', function () {
      var newBrand = getSelectedBrand();
      if (newBrand && newBrand !== selectedBrand) {
        selectedBrand = newBrand;
        lastFilteredBrand = null; /* force gallery re-filter */
        console.log('[InkDyno] Brand changed to:', selectedBrand);
        renderColorSwatches(selectedBrand);
      }
    });

    /* Also listen to Dawn's variant change pub/sub */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function () {
        /* CDN injector v5 re-injects after 350ms and fires
           inkdyno:cdn-images-injected which rebuilds colors.
           Here we just update brand state quickly. */
        setTimeout(function () {
          ensureHiddenInput();
          if (hiddenInput && selectedColor) {
            hiddenInput.value = selectedColor;
          }
          var newBrand = getSelectedBrand();
          if (newBrand) {
            selectedBrand = newBrand;
            lastFilteredBrand = null;
          }
        }, 100);
      });
    }
  }


  /* ═══════════════════════════════════════════════════════════
     INIT
     ═══════════════════════════════════════════════════════════ */

  /* ═══════════════════════════════════════════════════════════
     CDN IMAGES SUPPORT
     When cdn-gallery-injector.js adds CDN images to the DOM,
     rebuild available colors and re-apply brand filter.
     ═══════════════════════════════════════════════════════════ */
  document.addEventListener('inkdyno:cdn-images-injected', function (e) {
    console.log('[InkDyno] CDN images injected:', e.detail.count, '— rebuilding colors');
    buildAvailableColors();
    if (selectedBrand) {
      lastFilteredBrand = null; /* force re-filter */
      renderColorSwatches(selectedBrand);
    }
  });

  /* ═══════════════════════════════════════════════════════════
     9. GALLERY ↔ SWATCH SYNC
     When user clicks a thumbnail or slides the gallery,
     detect which image is now active and highlight its color swatch.
     ═══════════════════════════════════════════════════════════ */
  var isSyncingFromGallery = false;

  function syncSwatchFromMedia(mediaItem) {
    if (!mediaItem) return;
    var img = mediaItem.querySelector('img');
    if (!img) return;
    var meta = parseImageAlt(img.alt || img.getAttribute('alt') || '');
    if (!meta.color || !meta.brand) return;

    /* Only sync if same brand is active */
    if (meta.brand.toLowerCase() !== (selectedBrand || '').toLowerCase()) return;

    /* Already selected? Skip */
    if (selectedColor === meta.color) return;

    /* Find matching swatch and visually select it (without re-filtering gallery) */
    var container = document.getElementById('inkdyno-color-swatches');
    if (!container) return;

    var matchBtn = null;
    container.querySelectorAll('.inkdyno-swatch-btn').forEach(function (btn) {
      if (btn.getAttribute('data-color-name') === meta.color) {
        matchBtn = btn;
      }
    });

    if (matchBtn) {
      /* Update internal state */
      selectedColor = meta.color;

      /* Update swatch highlight */
      container.querySelectorAll('.inkdyno-swatch-btn').forEach(function (btn) {
        btn.classList.remove('inkdyno-swatch--selected');
      });
      matchBtn.classList.add('inkdyno-swatch--selected');

      /* Update label */
      var nameSpan = document.getElementById('inkdyno-selected-color-name');
      if (nameSpan) nameSpan.textContent = meta.color;

      /* Update hidden input */
      ensureHiddenInput();
      if (hiddenInput) hiddenInput.value = meta.color;

      console.log('[InkDyno] Swatch synced from gallery →', meta.color);
    }
  }

  function watchGallerySlideChanges() {
    var gallery = document.querySelector('media-gallery');
    if (!gallery) return;

    var viewer = gallery.querySelector('[id^="GalleryViewer"]');
    if (!viewer) return;

    /* Dawn's slider-component fires 'slideChanged' when visible slide changes */
    viewer.addEventListener('slideChanged', function (e) {
      if (e.detail && e.detail.currentElement) {
        syncSwatchFromMedia(e.detail.currentElement);
      }
    });

    /* Also handle direct clicks on gallery images (for non-slider layouts) */
    viewer.addEventListener('click', function (e) {
      var mediaItem = e.target.closest('[data-media-id]');
      if (mediaItem) {
        setTimeout(function () {
          syncSwatchFromMedia(mediaItem);
        }, 100);
      }
    });

    /* Handle thumbnail clicks — sync after Dawn activates the media */
    var thumbContainer = gallery.querySelector('[id^="GalleryThumbnails"]');
    if (thumbContainer) {
      thumbContainer.addEventListener('click', function (e) {
        var btn = e.target.closest('button');
        if (!btn) return;
        var li = btn.closest('[data-target]');
        if (!li) return;
        var targetMediaId = li.getAttribute('data-target');
        if (!targetMediaId) return;

        /* Find corresponding gallery item and sync after Dawn scrolls */
        setTimeout(function () {
          var galleryItem = viewer.querySelector('[data-media-id="' + targetMediaId + '"]');
          if (galleryItem) {
            syncSwatchFromMedia(galleryItem);
          }
        }, 200);
      });
    }

    console.log('[InkDyno] Gallery ↔ swatch sync active');
  }


  /* ═══════════════════════════════════════════════════════════
     FIX: Replace <legend> with <div> for consistent label height.
     Shopify CDN may cache old Liquid that still uses <legend>.
     Browsers ignore height/overflow on <legend> elements, causing
     label to grow/shrink with text length → color circles shift.
     ═══════════════════════════════════════════════════════════ */
  function fixColorLabel() {
    var selector = document.getElementById('inkdyno-color-selector');
    if (!selector) return;
    var legend = selector.querySelector('legend');
    if (!legend) return;
    var div = document.createElement('div');
    div.className = 'form__label inkdyno-color-label';
    div.innerHTML = legend.innerHTML;
    legend.parentNode.replaceChild(div, legend);
    console.log('[InkDyno] Replaced <legend> with <div> for fixed label height');
  }

  function init() {
    /* Only activate on T-Shirt product pages with brand color data */
    if (!loadBrandColors()) return;

    var vs = document.querySelector('variant-selects');
    if (!vs) return;

    console.log('[InkDyno] Brand + Color System v15.3 — contrast-filtered palette');
    injectCSS();
    fixColorLabel();
    ensureHiddenInput();

    /* Detect initial brand */
    selectedBrand = getSelectedBrand();
    if (selectedBrand && brandColors[selectedBrand]) {
      renderColorSwatches(selectedBrand);
    } else {
      /* If brand detection fails, show first brand's colors */
      var firstBrand = Object.keys(brandColors)[0];
      if (firstBrand) {
        selectedBrand = firstBrand;
        renderColorSwatches(firstBrand);
      }
    }

    watchBrandChanges();
    watchGallerySlideChanges();

    /* Watch modal open to re-apply brand filter on modal images */
    var productModal = document.querySelector('product-modal');
    if (productModal) {
      var mo = new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          if (m.attributeName === 'open' && productModal.hasAttribute('open') && selectedBrand) {
            /* Force re-filter modal images when modal opens */
            var savedBrand = lastFilteredBrand;
            lastFilteredBrand = null;
            filterGalleryByBrand(selectedBrand);
            console.log('[InkDyno] Modal opened → re-filtered for', selectedBrand);
          }
        });
      });
      mo.observe(productModal, { attributes: true, attributeFilter: ['open'] });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
